import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
    useDispatch,
    useSelector,
} from "react-redux";
import {
    AppDispatch,
    RootState,
} from "../../../../core/store/store";
import { Column } from "../../../../core/components/Column";
import { TextComponent } from "../../../../core/components/TextComponent";
import { Row } from "../../../../core/components/Row";
import { ArrowDown2 } from "../../../../core/icons/ArrowDown";
import { CustomButton } from "../../../../core/components/CustomButton";
import { ImageUploader } from "../../../../core/components/ImageUploader";
import { EditProfileProps, ProfileInfo } from "../interfaces/EditProfileInterface";
import { ErrorMessage, Field, Form, Formik, FormikHelpers, FormikProps } from "formik";
import { Select } from "../../../../core/components/Select";
import { Label } from "../../../../core/components/Label";
import ProfileViewEdit from "./ProfileViewEdit";
import { EditProfileFormValues, editprofileValidationSchema } from "../interfaces/EditProfileFormInterace";
import { onSendProfileConfig, onUpdateUser } from "../store/OnboardingThunk";
import UserInfo from "../../domain/entities/user";
import UserProfile from "../../domain/entities/getUser";
import { UpdateUserParams } from "../../domain/usecases/updateUser";
import { SendProfileConfigParams } from "../../domain/usecases/sendProfileConfig";
import { compressImage } from "../../../../core/constants/constants";
import { apiServices, getApiErrorMessage } from "../../../../core/utilities/ApiServices";
import { setSavedAboutMe } from "../store/OnboardingSlice";
import { useAboutMeAutosave } from "../hook/useAboutMeAutosave";

const duplicateProfilePhotoMessage =
    "That photo is already being used by another profile. Please choose a different photo.";

export const normalizeEditProfilePhotoMessage = (message?: string) => {
    const cleaned = (message || "").trim();
    if (!cleaned) return "We could not update your profile photo. Please try again.";

    const normalized = cleaned.toLowerCase();
    const isDuplicatePhotoMessage =
        normalized.includes("profile photos cannot be used") ||
        normalized.includes("duplicate profile photo") ||
        (normalized.includes("photo") &&
            normalized.includes("already") &&
            normalized.includes("profile"));

    return isDuplicatePhotoMessage ? duplicateProfilePhotoMessage : cleaned;
};

export const EditProfile: React.FC<EditProfileProps> = ({ hiddenEditProfile, handleEditProfile }) => {
    const dispatch: AppDispatch = useDispatch();
    const cantImages = 9;
    const maxLengthBIO = 1000;
    const maxInterest = 50;
    const [isSaveChanges, setIsSaveChanges] = useState<boolean>(false);
    const [isUploadingImage, setIsUploadImage] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [interest, setInterest] = useState<string>("");
    const menuRef = useRef<HTMLElement>(null);
    const profileViewEditRef = useRef<HTMLElement>(null);
    const { user, getProfileConfig, desiresList, sexualOrientationList, genderList, maritialList } = useSelector((state: RootState) => state.onboarding);
    const aboutMeSession = useMemo(() => {
        try { return window.localStorage.getItem("userToken"); } catch { return null; }
        // Capture the session for this member; never send an old draft as a new sign-in.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);
    const saveAboutMe = useCallback(async (bio: string) => {
        const userId = user?.id;
        const session = aboutMeSession;
        if (!userId || !session || window.localStorage.getItem("userToken") !== session) {
            throw new Error("Your sign-in changed. Please reopen your profile before saving.");
        }
        await editprofileValidationSchema.validateAt("bio", { bio });
        const data = new FormData();
        data.append("bio", bio);
        try {
            const response = await apiServices.put("/auth/update", data, {
                headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${session}` },
            });
            if (window.localStorage.getItem("userToken") !== session) throw new Error("Your sign-in changed. Please reopen your profile.");
            if (response.status !== 200 || response.data?.id !== userId || response.data?.bio !== bio) {
                throw new Error("The saved introduction could not be verified. Please try again.");
            }
            dispatch(setSavedAboutMe({ userId, bio }));
            return bio;
        } catch (reason) {
            throw new Error(getApiErrorMessage(reason, "Your introduction has not saved. Please try again."));
        }
    }, [dispatch, user?.id, aboutMeSession]);
    const aboutMe = useAboutMeAutosave({ userId: user?.id || "", savedBio: user?.bio || "", save: saveAboutMe });
    const profileInfo = (user?.profile_pictures?.map(picture => ({
        id: picture.id,
        url: picture.url,
        state: picture.is_public ? 1 : 0,
        is_public: picture.is_public,
        is_profile: picture.is_profile,
        create: picture.created_at,
    })) || []).sort((a, b) => {
        if (a.is_profile !== b.is_profile) return a.is_profile ? -1 : 1;
        return new Date(a.create).getTime() - new Date(b.create).getTime();
    });
    const initialValues: EditProfileFormValues = {
        gender: user?.gender_id || "",
        sexualOrientation: user?.sexual_orientation_id || "",
        lookingFor: getProfileConfig?.user_has_desire?.map(item => item.desire_id) || [],
        status: user?.marital_status_id || "",
        interest: getProfileConfig?.user_has_interest?.map(item => item.interest) || [],
    };
    const [labels, setLabels] = useState<string[]>(getProfileConfig?.user_has_interest?.map(item => item.interest) || []);
    const [files, setFiles] = useState<File[]>([]);
    const [iProfileInfoChange, setIProfileInfoChange] = useState<ProfileInfo | undefined>(undefined);
    const [selectImage, setSelectImage] = useState<ProfileInfo | undefined>(undefined);
    const [isSelectImage, setIsSelectImage] = useState<boolean>(false);
    const formikRef = useRef<FormikProps<EditProfileFormValues>>(null);

    useEffect(() => {
        let updateData: Partial<UserInfo> = {};

        if (iProfileInfoChange) {
            switch (iProfileInfoChange.state) {
                case 1:
                    updateData = { photosToPublic: [iProfileInfoChange.id] };
                    break;
                case 0:
                    updateData = { photosToPrivate: [iProfileInfoChange.id] };
                    break;
                case -1:
                    updateData = { filesToRemove: [iProfileInfoChange.id] };
                    break;
                case 2:
                    updateData = {
                        setAsProfile: iProfileInfoChange.id,
                        photosToPublic: [iProfileInfoChange.id]
                    };
                    break;
                default:
                    break;
            }
        }

        if (files && files.length > 0) {
            updateData = { ...updateData, files };
        }

        if (Object.keys(updateData).length > 0) {
            setError("");
            setIsUploadImage(true);
            const updateParams = new UpdateUserParams(updateData as UserInfo);
            void (async () => {
                try {
                    const updateResponse = await dispatch(onUpdateUser(updateParams));
                    if (updateResponse.meta.requestStatus === "rejected") {
                        throw new Error(getUpdateErrorMessage(updateResponse));
                    }

                    setIProfileInfoChange(undefined);
                    setFiles([]);
                    const successMessage = getUpdateSuccessMessage(updateResponse);
                    if (successMessage) {
                        setError(successMessage);
                    }
                } catch (error) {
                    setError(
                        normalizeEditProfilePhotoMessage(
                            error instanceof Error && error.message
                                ? error.message
                                : "Error updating profile"
                        )
                    );
                } finally {
                    setIsUploadImage(false);
                }
            })();
        }

    }, [files, iProfileInfoChange, dispatch]);

    const handleResetEditProfile = useCallback(() => {
        setLabels(getProfileConfig?.user_has_interest?.map(item => item.interest) ?? []);
        setInterest("");
        formikRef.current?.resetForm();
    }, [getProfileConfig]);

    const handleCloseEditProfile = useCallback(() => {
        if (!isSaveChanges) {
            void aboutMe.flush();
            handleResetEditProfile();
            handleEditProfile();
        }
    }, [isSaveChanges, handleEditProfile, handleResetEditProfile, aboutMe.flush]);

    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (isSelectImage) {
            if (profileViewEditRef.current && !profileViewEditRef.current.contains(event.target as Node)) {
                setIsSelectImage(false);
            }
        } else {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                handleCloseEditProfile();
            }
        }
    }, [isSelectImage, handleCloseEditProfile]);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    const handleAddInterest = async (setFieldError: (field: string, message: string) => void) => {
        try {
            await editprofileValidationSchema.validateAt("interest[0]", { interest: [interest] });
            if (interest.trim() === "") return;
            if (labels.includes(interest.trim())) {
                setFieldError("interest", "Duplicate interests are not allowed");
                return;
            }
            setLabels([...labels, interest.trim()]);
            setInterest(""); 
            setFieldError("interest", "");
        } catch (error: any) {
            setFieldError("interest", error.message);
        }
    };

    const handleDeleteInterest = (interestToDelete: string) => {
        setLabels(labels.filter(label => label !== interestToDelete));
    };

    const handleEditStateImage = (updatedImage: ProfileInfo) => {
        setIProfileInfoChange(updatedImage)
        setSelectImage(undefined);
        setIsSelectImage(false);
    }

    const getUpdateErrorMessage = (response: any) => {
        return "error" in response && response.error?.message
            ? response.error.message
            : "Error updating profile";
    }

    const getUpdateSuccessMessage = (response: any) => {
        const message =
            response?.payload && typeof response.payload.message === "string"
                ? response.payload.message.trim()
                : "";
        return message || null;
    }

    const handleReplaceImage = async (file: File, image: ProfileInfo) => {
        if (isUploadingImage) {
            return;
        }
        if (!image.id) {
            setError("Error updating profile");
            return;
        }

        setError("");
        setIsUploadImage(true);
        const originalImageIds = new Set(profileInfo.map((picture) => picture.id));

        try {
            let uploadFile = file;
            try {
                uploadFile = await compressImage(file, {
                    quality: 0.9,
                    maxWidth: 1600,
                    maxHeight: 1600,
                });
            } catch {
                uploadFile = file;
            }

            const uploadResponse = await dispatch(
                onUpdateUser(new UpdateUserParams({ files: [uploadFile] }))
            );
            if (uploadResponse.meta.requestStatus === "rejected") {
                throw new Error(getUpdateErrorMessage(uploadResponse));
            }

            const uploadMessage = getUpdateSuccessMessage(uploadResponse);
            if (uploadMessage) {
                setError(uploadMessage);
                return;
            }

            const updatedUser = uploadResponse.payload as UserProfile | undefined;
            const replacementImage = [...(updatedUser?.profile_pictures ?? [])]
                .filter((picture) => picture.id && !originalImageIds.has(picture.id))
                .sort((a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )[0];

            if (!replacementImage?.id) {
                throw new Error("Error updating profile");
            }

            const replaceResponse = await dispatch(
                onUpdateUser(
                    new UpdateUserParams({
                        setAsProfile: replacementImage.id,
                        photosToPublic: [replacementImage.id],
                        filesToRemove: [image.id],
                    })
                )
            );
            if (replaceResponse.meta.requestStatus === "rejected") {
                throw new Error(getUpdateErrorMessage(replaceResponse));
            }

            setSelectImage(undefined);
            setIsSelectImage(false);
        } catch (error) {
            setError(
                normalizeEditProfilePhotoMessage(
                    error instanceof Error && error.message
                        ? error.message
                        : "Error updating profile"
                )
            );
        } finally {
            setIsUploadImage(false);
        }
    }

    const handleIsSelected = () => {
        setIsSelectImage(!isSelectImage)
    }

    const handleViewEditImage = (index: number) => {
        setSelectImage(profileInfo[index]);
        handleIsSelected();
    }

    const handleSubmit = async (values: EditProfileFormValues, { setSubmitting }: FormikHelpers<EditProfileFormValues>) => {
        setIsSaveChanges(true);
        setError("");
        if (!await aboutMe.flush()) {
            setIsSaveChanges(false);
            setSubmitting(false);
            return;
        }
        const isProfileConfigChanged = JSON.stringify({
            desires: values.lookingFor,
            interests: labels
        }) !== JSON.stringify({
            desires: getProfileConfig?.user_has_desire?.map(item => item.desire_id) || [],
            interests: getProfileConfig?.user_has_interest?.map(item => item.interest) || []
        });
        const isUserInfoChanged = JSON.stringify({
            gender_id: values.gender,
            sexual_orientation_id: values.sexualOrientation,
            marital_status_id: values.status
        }) !== JSON.stringify({
            gender_id: user?.gender_id || "",
            sexual_orientation_id: user?.sexual_orientation_id || "",
            marital_status_id: user?.marital_status_id || ""
        });
        if (!isProfileConfigChanged && !isUserInfoChanged) {
            setIsSaveChanges(false);
            handleResetEditProfile();
            handleEditProfile();
            return setSubmitting(false);
        }
        try {
            if (isProfileConfigChanged) {
                const profileConfigResponse = await dispatch(
                    onSendProfileConfig(
                        new SendProfileConfigParams({
                            desires: values.lookingFor,
                            interests: labels,
                        })
                    )
                );
                if (profileConfigResponse.meta.requestStatus === "rejected") {
                    const errorMessage =
                        "error" in profileConfigResponse && profileConfigResponse.error?.message
                            ? profileConfigResponse.error.message
                            : "Error updating profile";
                    throw new Error(
                        errorMessage
                    );
                }
            }
            if (isUserInfoChanged) {
                const updateUserResponse = await dispatch(
                    onUpdateUser(
                        new UpdateUserParams({
                            gender_id: values.gender,
                            sexual_orientation_id: values.sexualOrientation,
                            marital_status_id: values.status,
                        })
                    )
                );
                if (updateUserResponse.meta.requestStatus === "rejected") {
                    const errorMessage =
                        "error" in updateUserResponse && updateUserResponse.error?.message
                            ? updateUserResponse.error.message
                            : "Error updating profile";
                    throw new Error(
                        errorMessage
                    );
                }
            }
            handleEditProfile();
        } catch (error) {
            setError(
                error instanceof Error && error.message
                    ? error.message
                    : "Error updating profile"
            );
        } finally {
            setInterest("");
            formikRef.current?.resetForm();
            setIsSaveChanges(false);
            setSubmitting(false);
        }
    };

    return (
        <>
            {(hiddenEditProfile || isSelectImage) &&
                <div className="max-sm:hidden fixed top-0 left-0 z-20 h-screen w-full bg-[#00000099]"></div>
             }
            {isSelectImage &&
                <ProfileViewEdit
                    Ref={profileViewEditRef}
                    selectImage={selectImage}
                    setSelectImage={setSelectImage}
                    handleIsSelected={handleIsSelected}
                    handleEditStateImage={handleEditStateImage}
                    handleReplaceImage={handleReplaceImage}
                    isReplacingImage={isUploadingImage}
                />
            }
            <aside
                {...(hiddenEditProfile ? { ref: menuRef } : {})}
                className={`fixed h-full overflow-y-auto top-0 right-0 z-20 md:z-30 p-5 md:p-10 w-full sm:w-[28.5rem] transition-transform ${hiddenEditProfile ? "translate-x-0" : "translate-x-full"} bg-white`}
            >
                <Column className="gap-2 md:gap-4">
                    <Row
                        className="mb-0.5 gap-3"
                        crossAxisAlignment="center"
                    >
                        <button
                            type="button"
                            onClick={handleCloseEditProfile}
                            className="w-10 aspect-square flex items-center justify-center text-primary transform rotate-90 rounded-full hover:bg-grayDefault"
                            aria-label="Back to profile"
                        >
                            <ArrowDown2 />
                        </button>
                        <TextComponent variant="h4" className="font-semibold">
                            Edit Profile
                        </TextComponent>
                    </Row>
                    <Column className="gap-5 md:gap-10">
                        <ImageUploader
                            className="w-full"
                            files={files}
                            setFiles={(newFiles: File[] | null) => setFiles(newFiles || [])}
                            inputId="fileInput"
                            columns={2}
                            slots={cantImages}
                            multiple={true}
                            iProfileInfo={profileInfo}
                            setIProfileInfo={setIProfileInfoChange}
                            handleViewEditImage={handleViewEditImage}
                            loading={isUploadingImage}
                            cropToSquareOnUpload
                        />
                    </Column>
                    <Column>
                        <Formik
                            innerRef={formikRef} 
                            enableReinitialize={true}
                            initialValues={initialValues}
                            validationSchema={editprofileValidationSchema}
                            validateOnBlur
                            validateOnChange={false}
                            onSubmit={handleSubmit}
                        >
                            {({ handleChange, handleBlur, values, setFieldError }) => (
                                <Form className="flex flex-col mt-5 text-start gap-2 md:gap-5">
                                    <Column className="gap-2">
                                        <Field
                                            as={Select}
                                            id="gender"
                                            name="gender"
                                            label="Select your Gender"
                                            items={genderList.map((e) => ({ key: e.id, label: e.name }))}
                                            defaultSelectedKeys={[initialValues.gender]}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        <ErrorMessage name="gender" component="div" className="text-red-500 text-xs" />
                                    </Column>
                                    <Column className="gap-2">
                                        <Field
                                            as={Select}
                                            id="sexualOrientation"
                                            name="sexualOrientation"
                                            label="My Sexual Orientation is"
                                            items={sexualOrientationList.map((e) => ({ key: e.id, label: e.name }))}
                                            defaultSelectedKeys={[initialValues.sexualOrientation]}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        <ErrorMessage name="sexualOrientation" component="div" className="text-red-500 text-xs" />
                                    </Column>
                                    <Column className="gap-2">
                                        <Field
                                            as={Select}
                                            id="lookingFor"
                                            name="lookingFor"
                                            selectionMode="multiple"
                                            label="I am Looking for"
                                            items={desiresList.map((e) => ({ key: e.id, label: e.name }))}
                                            defaultSelectedKeys={initialValues.lookingFor}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        <ErrorMessage name="lookingFor" component="div" className="text-red-500 text-xs" />
                                    </Column>
                                    <Column className="gap-2">
                                        <Field
                                            as={Select}
                                            id="status"
                                            name="status"
                                            label="My Status is"
                                            items={maritialList.map((e) => ({ key: e.id, label: e.name }))}
                                            defaultSelectedKeys={[initialValues.status]}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        <ErrorMessage name="status" component="div" className="text-red-500 text-xs" />
                                    </Column>
                                    <Column className="gap-2">
                                        <TextComponent variant="h6">
                                            About You
                                        </TextComponent>
                                        <textarea
                                            id="bio"
                                            name="bio"
                                            className="outline-none border border-[#E6E6E6] p-3 h-32 resize-none w-full"
                                            placeholder="Enter About You"
                                            onChange={(event) => aboutMe.change(event.target.value)}
                                            onBlur={() => { void aboutMe.flush(); }}
                                            value={aboutMe.value}
                                            maxLength={maxLengthBIO}
                                        />
                                        <p role="status" className="text-sm text-graySecondary">
                                            {aboutMe.status === "saving" ? "Saving…" : aboutMe.status === "saved" ? "Saved automatically" : aboutMe.status === "error" ? "Not saved yet" : "Saves automatically"}
                                        </p>
                                        {aboutMe.error && <div role="alert" className="text-red-500 text-xs">
                                            {aboutMe.error} <button type="button" className="underline" onClick={() => { void aboutMe.flush(true); }}>Retry</button>
                                        </div>}
                                        <TextComponent variant="h6" className="text-right text-graySecondary">
                                            {aboutMe.value.length}/{maxLengthBIO}
                                        </TextComponent>
                                    </Column>
                                    <Column className="gap-2">
                                        <Row mainAxisAlignment="between">
                                            <TextComponent variant="h6">
                                                Add Interest
                                            </TextComponent>
                                            <button type="button" className="max-md:hidden" onClick={ () => { handleAddInterest(setFieldError) }}>
                                                <TextComponent variant="h6" className="text-primary hover:text-pink font-semibold">
                                                    + Add More
                                                </TextComponent>
                                            </button>
                                        </Row>
                                        <Column className="gap-2">
                                            <Field
                                                as="input"
                                                id="interest"
                                                name="interest"
                                                className="outline-none border border-[#E6E6E6] p-3 w-full"
                                                placeholder="Enter Your Interest"
                                                value={interest}
                                                onChange={(e: any) => setInterest(e.target.value)}
                                                onBlur={handleBlur}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter" || e.key === ",") {
                                                        e.preventDefault();
                                                        handleAddInterest(setFieldError);
                                                    }
                                                }}
                                                maxLength={maxInterest}
                                            />
                                            <ErrorMessage name="interest" component="div" className="text-red-500 text-xs" />
                                            <TextComponent variant="h6" className="text-right text-graySecondary">
                                                {interest.length}/{maxInterest}
                                            </TextComponent>
                                        </Column>
                                        <Row className="gap-2.5 flex-wrap">
                                            {labels?.map((label, index) => (
                                                <Label key={index} title={label} onClick={() => handleDeleteInterest(label)} />
                                            ))}
                                        </Row>
                                    </Column>
                                    <Column className="gap-2">
                                        <CustomButton type="submit" className="mt-4" loading={isSaveChanges}>
                                            Save Changes
                                        </CustomButton>
                                        {error &&
                                            <TextComponent variant="h7" className="text-red-500 text-center">
                                                {error}
                                            </TextComponent>
                                        }
                                    </Column>
                                </Form>
                            )}
                        </Formik>
                    </Column>
                </Column>
            </aside>
        </>
    );
};
